// One stylesheet entry. See index.css for why the order is declared there
// rather than as separate imports here.
import './index.css'

import * as api from './api'
import {
  audioOnlyFromLocation,
  buildConfig,
  devConfigOverride,
  embedIdFromLocation,
  type PlayerConfig,
} from './config'
import { recallSession } from './ids'
import { OverlayManager } from './overlays'
import { PlayerController } from './controller'
import { whenCastSdkReady } from './player'
import { mountPasswordPrompt } from './password-prompt'

/**
 * Boot.
 *
 * The page is one static shell, identical for every embed — there is no
 * templated config any more. Everything the player needs comes from a single
 * authorize call, which answers "what does this embed look like" and "may this
 * viewer watch it" together.
 */
/**
 * Origin of the page this player is embedded in, for the domain allowlist.
 *
 * `ancestorOrigins` is exact where it exists (Chromium, WebKit). We take the
 * LAST entry, the top-level document, rather than the immediate parent: the
 * site the viewer is actually on is what the rule is about, and reporting the
 * immediate parent would let an unauthorised page pass by nesting an
 * authorised one inside itself.
 *
 * `document.referrer` is the cross-browser fallback, notably for Firefox.
 * Under the default referrer policy a cross-origin iframe is still told the
 * parent's origin, which is all we need; it names the immediate parent rather
 * than the top-level page, which is the best that signal can do.
 *
 * A parent sending `Referrer-Policy: no-referrer` leaves us with neither, and
 * we send nothing. The server decides what an unknown parent means.
 */
function embeddingPageOrigin(): string | undefined {
  if (window.self === window.top) return undefined
  try {
    const ancestors = window.location.ancestorOrigins
    if (ancestors && ancestors.length > 0) {
      const top = ancestors[ancestors.length - 1]
      if (top && top !== 'null') return top
    }
  } catch {
    /* not implemented here — fall through to the referrer */
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin
  } catch {
    /* opaque or malformed */
  }
  return undefined
}

async function boot(): Promise<void> {
  const app = document.querySelector<HTMLElement>('.app')
  if (!app) {
    console.error('[player] .app container missing from embed page')
    return
  }

  // Dev / self-host: a page may still template its own config and skip the API
  // entirely. This is also the rollback path (§15) — with preloadHlsUrl set the
  // player behaves exactly as it did before watch-guard existed.
  const override = devConfigOverride()
  if (override) {
    await startPlayer(app, override, null)
    return
  }

  const embedId = embedIdFromLocation()
  if (!embedId) {
    console.error('[player] no embedId in the URL — nothing to play')
    return
  }

  const result = await api.authorize(embedId, {
    framed: window.self !== window.top,
    ...(embeddingPageOrigin() ? { referrerUrl: embeddingPageOrigin() } : {}),
    audioOnly: audioOnlyFromLocation(),
    sessionId: recallSession(embedId),
  })

  if (result.ok) {
    const config = configFrom(result.config, embedId)
    await startPlayer(app, config, result.playback)
    return
  }

  if (result.kind === 'fault') {
    // Not a policy answer — the service is unreachable or broken. Say so
    // plainly rather than implying the broadcaster refused them.
    renderFault(app, embedId)
    return
  }

  // A denial still carries branding, which is the whole reason the player
  // renders these states instead of a 403 page.
  const config = configFrom(result.config, embedId)
  const overlays = new OverlayManager(app, config)
  const controller = new PlayerController(app, config, overlays)

  // A refused viewer still owns a controller with live listeners and, for
  // recheckable denials, a timer. Tear it down with the page like any other.
  window.addEventListener('pagehide', () => controller.stop())

  if (result.reason === 'password_required' || result.reason === 'password_incorrect') {
    mountGate(app, config, controller, {
      // `password_required` is the service explaining why the gate is up, and
      // it becomes the gate's subtitle. `password_incorrect` is an attempt that
      // failed, which belongs under the field instead.
      message: result.reason === 'password_required' ? result.message : null,
      initialError: result.reason === 'password_incorrect' ? result.message : null,
    })
    return
  }

  controller.renderDenial(result.reason, result.message)
}

function configFrom(raw: api.RawPlayerConfig, embedId: string): PlayerConfig {
  return buildConfig(raw, { embedId, audioOnly: audioOnlyFromLocation() })
}

/** How long a viewer may wait on the Cast SDK before we mount without it. */
const CAST_SDK_WAIT_MS = 1500

async function startPlayer(
  app: HTMLElement,
  config: PlayerConfig,
  grant: api.PlaybackGrant | null,
): Promise<void> {
  // Mounting before the Cast SDK lands costs the viewer their cast button for
  // the whole session, and the only cure afterwards is a rebuild we refuse to
  // do mid-playback. Waiting a beat here is the cheaper trade.
  if (!config.audioOnly) await whenCastSdkReady(CAST_SDK_WAIT_MS)

  const overlays = new OverlayManager(app, config)
  const controller = new PlayerController(app, config, overlays, grant)
  controller.start()
  window.addEventListener('pagehide', () => controller.stop())
}

/**
 * The password gate runs BEFORE any player exists: authorize refused, so there
 * is no source and there may never be one. On success the controller takes over
 * with the grant that the correct password produced.
 */
function mountGate(
  app: HTMLElement,
  config: PlayerConfig,
  controller: PlayerController,
  copy: { message: string | null; initialError: string | null },
): void {
  const gate = mountPasswordPrompt(app, {
    title: config.title,
    message: copy.message,
    checkLive: () => api.fetchLiveStatus(config.embedId),
    onSubmit: async (password) => {
      const result = await api.authorize(config.embedId, {
        framed: window.self !== window.top,
        ...(embeddingPageOrigin() ? { referrerUrl: embeddingPageOrigin() } : {}),
        audioOnly: config.audioOnly,
        password,
        sessionId: recallSession(config.embedId),
      })

      if (result.ok) {
        gate.dismiss()
        if (!config.audioOnly) await whenCastSdkReady(CAST_SDK_WAIT_MS)
        controller.adoptBootGrant(result.playback)
        controller.start()
        window.addEventListener('pagehide', () => controller.stop())
        return
      }

      if (result.kind === 'fault') {
        gate.setError('Could not reach the server. Try again.')
        return
      }

      if (result.retryAfterSeconds) {
        gate.setCooldown(result.retryAfterSeconds)
        return
      }

      if (result.reason === 'password_incorrect' || result.reason === 'password_required') {
        gate.setError(result.message || 'Incorrect password')
        return
      }

      // Refused for a different reason now — replace the gate with that state.
      gate.dispose()
      controller.renderDenial(result.reason, result.message)
    },
  })

  if (copy.initialError) gate.setError(copy.initialError)
}

/** Transport failure: nothing to brand with, so keep it factual. */
function renderFault(app: HTMLElement, embedId: string): void {
  console.error(`[player] could not reach watch-guard for ${embedId}`)
  const overlays = new OverlayManager(app, buildConfig({}, { embedId }))

  let retryTimer: ReturnType<typeof setTimeout> | null = null
  const retry = () => {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
    overlays.showOffline({
      tone: 'retrying',
      label: 'Reconnecting',
      title: 'Getting the stream',
      subtitle: 'One moment',
    })
    void boot()
  }

  overlays.showOffline({
    tone: 'error',
    label: 'Unavailable',
    title: 'We could not load this stream',
    subtitle: 'Check your connection, then try again',
    // It retries on its own, but 15s of nothing is a long time to sit in front
    // of. Let someone who is already looking at it skip the wait.
    action: { label: 'Try again', onClick: retry },
  })

  retryTimer = setTimeout(retry, 15_000)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot())
} else {
  void boot()
}
